import {Injectable} from '@angular/core';
import {CometChat} from '@cometchat-pro/cordova-ionic-chat/CometChat';
import {Observable, Subject, Subscription} from 'rxjs';
import {IFileToSend} from '../models/IFileToSend';
import {environment} from '../../environments/environment';
import {ICheckin} from '../models/ICheckin';
import {HttpClient} from '@angular/common/http';
import {each, filter, groupBy, map} from 'lodash';
import {IObjectMap} from '../models/IObjectMap';
import {UserService} from './user.service';
import {NavController, Platform, ToastController} from '@ionic/angular';
import {PushNotificationService} from './push-notification.service';
import {TranslationService} from '../translation';
import {IAppState} from '../models/IAppState';
import {Store} from '@ngrx/store';
import {IUserData, MemberType} from '../models/IUserData';
import {SoundService} from './sound.service';
import {DesktopNotificationService} from './desktop-notification.service';
import User = CometChat.User;

@Injectable({
  providedIn: 'root'
})
export class CometchatService {
  public user: CometChat.User;
  private authToken: string;
  private apiKey = environment.cometApiKey;
  private appRegion = 'eu';
  private typingTimeout;
  private hasInit = false;
  private $readStatusChange = new Subject();
  private state$: Observable<IAppState> = this.store.select((state) => state.AppStateReducer);
  private stateSub: Subscription;
  private isLoginPromise?: Promise<User>;
  public userData: IUserData;

  private unreadMessages: IObjectMap<CometChat.BaseMessage[]> = {};
  private markedAsReadMessageGroup: IObjectMap<number> = {};
  private readonly maxLimitMessages: number = 100;

  constructor(
    private http: HttpClient,
    private navCtrl: NavController,
    private pushNotificationService: PushNotificationService,
    private ts: TranslationService,
    private platform: Platform,
    private soundService: SoundService,
    private toastController: ToastController,
    private desktopNotificationService: DesktopNotificationService,
    private store: Store<{AppStateReducer: IAppState}>,
    private userService: UserService
  ) {
    this.stateSub = this.state$.subscribe(
      (appState: IAppState) => {
        if (appState.userData && !this.userData) {
          this.userData = appState.userData;
          this.init();
        }
      });

    this.platform.resume.subscribe(async () => {
      this.fetchUnreadMessages();
    });
  }

  public getUnreadMessagesInstant() {
    return this.unreadMessages;
  }

  async init() {
    if (this.hasInit) {
      return;
    }
    this.hasInit = true;
    this.clearCache();
    let appSetting: CometChat.AppSettingsBuilder;
    let buildSettings: CometChat.AppSettings;

    try {
      console.log('Create CometChat Instance');
      appSetting = new CometChat.AppSettingsBuilder().subscribePresenceForAllUsers();

      if (appSetting) {
        appSetting.setRegion(this.appRegion);
      }

      buildSettings = appSetting.build();
    } catch (e) {
      console.error(e);
      // retry after 10 sec in case of fail
      setTimeout(() => this.init(), 10000);
      return;
    }

    try {
      console.log('Init CometChat');
      await CometChat.init(environment.cometAppID, buildSettings);
    } catch (e) {
      console.error(e);
      // retry after 10 sec in case of fail
      setTimeout(() => this.init(), 10000);
      return;
    }

    try {
      console.log('CometChat userLogin');
      await this.userLogin();
    } catch (e) {
      console.error(e);
      // retry after 10 sec in case of fail
      setTimeout(() => this.init(), 10000);
      return;
    }

    this.fetchUnreadMessages();

    // fallback in case we have broken unreadMessages cache
    setInterval(() => {
      this.fetchUnreadMessages();
    }, 1000 * 30); // 30 sec

    this.subscribeNewMessages().subscribe((msg) => {
      this.fetchUnreadMessages();
      this.handleGlobalMessageArrived(msg);
    });

    this.subscribeEditedMessages().subscribe((msg) => {
      this.handleGlobalMessageArrived(msg);
    });
  }

  private async fetchUnreadMessages() {
    this.unreadMessages = await this.getUnreadMessages();
  }

  private getIcon(msg) {
    if (msg.data.attachments?.length > 0) {
      return 'assets/imgs/attachment.png';
    } else {
      return 'assets/imgs/logo.png';
    }
  }

  private handleGlobalMessageArrived(msg) {
    console.log('handleGlobalMessageArrived', msg);
    if (this.platform.is('ios')
      || this.platform.is('android')
    ) {
      return;
    }

    const sender = msg.getSender ? msg.getSender() : null;
    const receiver = msg.getReceiver ? msg.getReceiver() : null;
    const senderNameRaw = sender?.getName ? sender.getName() : sender?.name;
    const receiverNameRaw = receiver?.getName ? receiver.getName() : receiver?.name;
    const payloadTitleRaw = msg?.data?.title || msg?.data?.notification?.title;
    const senderName = (senderNameRaw || '').trim();
    const receiverName = (receiverNameRaw || '').trim();
    const payloadTitle = (payloadTitleRaw || '').trim();
    const defaultTitle = (this.ts.instant('New Chat Message') || 'New Chat Message').trim() || 'New Chat Message';
    const toastTitle = senderName || receiverName || payloadTitle || defaultTitle;
    const usedFallbackTitle = toastTitle !== senderName;

    const isCheckinOpened = window.location.href.indexOf(msg.getReceiverId()) > -1;
    const isCheckinsListScreen = window.location.href.indexOf('checkin-list') > -1;
    const isCompanyMember = this.userData && this.userData.memberType === MemberType.companyMember;

    const isLocationChatMember = this.user.getUid() === msg?.data?.metadata?.LocationChatMemberId;
    const isTheSameUser = this.user.getUid() === msg.getSender().getUid() && !isLocationChatMember;
    const payload = {
      checkinId: msg.getReceiverId()
    };

    let toastMessage = msg.getText ? msg.getText() : '';

    if (msg.data?.pushNotificationMessage?.length) {
      toastMessage = this.ts.instant(msg.data?.pushNotificationMessage);
    }

    console.log('handleGlobalMessageArrived: context', {
      messageId: msg.getId ? msg.getId() : msg?.id,
      category: msg.getCategory ? msg.getCategory() : msg?.category,
      type: msg.getType ? msg.getType() : msg?.type,
      receiverId: msg.getReceiverId ? msg.getReceiverId() : msg?.receiverId,
      senderId: sender?.getUid ? sender.getUid() : sender?.uid,
      senderName,
      senderNameRaw,
      receiverName,
      receiverNameRaw,
      payloadTitle,
      payloadTitleRaw,
      resolvedToastTitle: toastTitle,
      pushNotificationMessage: msg?.data?.pushNotificationMessage,
      isSilentNotification: msg?.data?.isSilentNotification,
      isCheckinOpened,
      isCheckinsListScreen,
      isCompanyMember,
      isLocationChatMember,
      isTheSameUser
    });
    console.log('toastMessage', toastMessage);
    console.log('toastTitle', toastTitle);

    if (usedFallbackTitle) {
      console.warn('handleGlobalMessageArrived: sender name is empty, using fallback notification title', {
        senderName,
        receiverName,
        payloadTitle,
        resolvedToastTitle: toastTitle
      });
    }

    if (msg.data?.isSilentNotification !== true) {
      this.desktopNotificationService.showDesktopNotification({
        text: toastTitle,
        body: toastMessage,
        icon: this.getIcon(msg),
        onClick: () => this.navCtrl.navigateForward(['/chat/' + payload.checkinId])
      });
    }

    if (isCheckinOpened || (!isCompanyMember && isCheckinsListScreen)) {
      this.navCtrl.navigateForward(['/chat/' + payload.checkinId]);
    } else if (!isTheSameUser && msg.data?.isSilentNotification !== true) {
      this.pushNotificationService.showChatPushNotification(payload, toastMessage, toastTitle);
      console.log('playLoud-income-notTheSameUser-', this.user.getUid(), msg.getSender().getUid());
      this.soundService.playLoud();
    }
  }

  public getUnreadMessagesCount() {
    return map(this.unreadMessages).length;
  }

  public getUnreadMessagesCountForGroup(groupId: string) {
    return this.unreadMessages[groupId] ? this.unreadMessages[groupId].length : 0;
  }

  clearCache() {
    for (const key in localStorage) {
      if (key.indexOf(environment.cometAppID) === 0 ) {
        localStorage.removeItem(key);
      }
    }
  }

  joinChat(checkInId: string): Observable<any> {
    console.log('joinChat', checkInId);
    return this.http.post<ICheckin>(`${environment.apiUrl}/checkins/${checkInId}/chat`, {});
  }

  async updateUser(user: CometChat.User) {
    try {
      await CometChat.updateUser(user, this.apiKey);
    } catch (e) {
      console.error(e);
    }
  }

  async getCometToken() {
    if (localStorage.cometChatToken) {
      return localStorage.cometChatToken;
    } else {
      await new Promise((r) => setTimeout(() => r(null), 1000));
      const chatToken = await this.userService.getChatToken().toPromise();
      localStorage.cometChatToken = chatToken.chatAuthToken;
    }

    return localStorage.cometChatToken;
  }

  async userLogin(): Promise<User> {
    if (this.isLoginPromise !== undefined) {
      return this.isLoginPromise;
    }

    if (this.user) {
      return this.user
    }

    let isLoginResolver;
    this.isLoginPromise = new Promise((resolve) => {
      isLoginResolver = resolve;
    })

    try {
      this.authToken = await this.getCometToken();
    } catch (e) {
      isLoginResolver(null);
    }

    let loggedInUser;
    this.user = null;
    try {
      loggedInUser = await CometChat.getLoggedinUser();
      this.user = loggedInUser;
    } catch (e) {
      this.user = null;
      console.warn('getLoggedInUser error', this.user);
    }

    if (!this.user) {
      try {
        this.user = await CometChat.login(this.authToken);
        isLoginResolver(this.user);
        return this.user;
      } catch (e) {
        this.user = null;
        console.warn('userLogin failed with exception:', JSON.stringify(e));
        isLoginResolver(null);
        return null;
      }
    } else {
      isLoginResolver(this.user);
      return this.user;
    }
  }

  async userLogout() {
    this.authToken = undefined;
    localStorage.removeItem('cometChatToken');
    try {
      const loggedInUser = await CometChat.getLoggedinUser();
      if (loggedInUser) {
        await CometChat.logout();
        this.clearCache();
      } else {
        this.clearCache();
        return;
      }
    } catch (e) {
      console.error(e);
    }
  }

  async getUnreadMessages(): Promise<IObjectMap<CometChat.BaseMessage[]>> {
    //     const messagesRequest = await CometChat.getUnreadMessageCountForAllGroups(true) as IObjectMap<number>;
    const messagesRequest = new CometChat.MessagesRequestBuilder()
      .setUnread(true)
      .setLimit(this.maxLimitMessages)
      .setCategory('message')
      .build();

    let unreadMessages = [];

    const res = await this.getAllUnreadMessages(messagesRequest);

    unreadMessages.push(...res);

    unreadMessages = filter(unreadMessages, (message) => {
      return message.getCategory() === 'message';
    });

    each(unreadMessages, (message) => {
      if (this.markedAsReadMessageGroup[message.getReceiverId()] >= message.id) {
        this.markAsRead(message.id, message.receiverId, this.user.getUid());
      }
    });

    unreadMessages = filter(unreadMessages, (message) => {
      return !this.markedAsReadMessageGroup[message.getReceiverId()]
        || message.getId() > this.markedAsReadMessageGroup[message.getReceiverId()];
    });

    return groupBy(unreadMessages, 'receiverId');
  }

  async searchMessages(keyword = '', timestamp = 0): Promise<CometChat.BaseMessage[] | []> {
    let messagesRequest = new CometChat.MessagesRequestBuilder()
      .setLimit(100)
      .setCategory('message');

    if (keyword) {
      messagesRequest = messagesRequest.setSearchKeyword(keyword);
    }

    if (timestamp) {
      messagesRequest = messagesRequest.setTimestamp(timestamp);
    }

    return await messagesRequest.build().fetchPrevious();
  }

  async sendMessage(guid: string, messageText: string, metadata?: any) {
    const receiverType = CometChat.RECEIVER_TYPE.GROUP;

    const textMessage = new CometChat.TextMessage(
      guid,
      messageText,
      receiverType
    );

    textMessage.setMetadata(metadata);

    return await CometChat.sendMessage(textMessage);
  }

  async fetchAllMessages(guid: string): Promise<CometChat.BaseMessage[]> {
    const limit = 100;

    try {
      const messagesRequest = new CometChat.MessagesRequestBuilder()
        .setGUID(guid)
        .setLimit(limit)
        .setCategory('message')
        .build();

      const messages = [];
      let count = limit;

      while (count === limit) {
        const res = await messagesRequest.fetchPrevious();
        count = res.length;
        messages.splice(0,0,...res);
      }

      const lastMsg = messages[messages.length - 1];

      if (lastMsg) {
        await CometChat.getMessageReceipts(lastMsg.getId());
      }

      return messages;
    } catch (error) {
      console.error(error);
      if (this.isGroupNotJoinedError(error)) {
        throw error;
      }
      return [];
    }
  }

  public isGroupNotJoinedError(error: any): boolean {
    return error?.code === 'ERR_GROUP_NOT_JOINED';
  }

  public async presentGroupNotJoinedToast(): Promise<void> {
    const toast = await this.toastController.create({
      message: this.ts.instant('You are not a member of this particular chat'),
      position: this.ts.instant('bottom'),
      duration: 3000,
      buttons: [{
        text: this.ts.instant('Ok'),
        // TODO: check class
        // cssClass: 'secondary',
        role: 'cancel'
      }]
    });
    toast.present();
  }

  subscribeIndications() {
    const listenerID = '' + Math.random();
    const $subj = new Subject<any>();

    CometChat.addMessageListener(
      listenerID,
      new CometChat.MessageListener({
        onTypingStarted: typingIndicator => {
          $subj.next(typingIndicator);
        },
        onTypingEnded: typingIndicator => {
          $subj.next(typingIndicator);
        },
        onMessagesDelivered: messageReceipt => {
          $subj.next(messageReceipt);
        },
        onMessagesRead: messageReceipt => {
          $subj.next(messageReceipt);
        }
      })
    );

    return $subj;
  }

  subscribeWriting() {
    const listenerID = '' + Math.random();
    const $subj = new Subject<any>();

    CometChat.addMessageListener(
      listenerID,
      new CometChat.MessageListener({
        onTypingStarted: typingIndicator => {
          $subj.next({
            data: typingIndicator,
            typing: true
          });
        },
        onTypingEnded: typingIndicator => {
          $subj.next({
            data: typingIndicator,
            typing: false
          });
        }
      })
    );

    return $subj;
  }

  subscribeNewMessages(): Observable<CometChat.BaseMessage> {
    const listenerID = '' + Math.random();
    const $subj = new Subject<CometChat.BaseMessage>();

    CometChat.addMessageListener(
      listenerID,
      new CometChat.MessageListener({
        onTextMessageReceived: textMessage => {
          $subj.next(textMessage);
        },
        onMediaMessageReceived: mediaMessage => {
          $subj.next(mediaMessage);
        },
        onCustomMessageReceived: customMessage => {
          $subj.next(customMessage);
        },
        onMessageDeleted: message => {
          $subj.next(message);
        },
        onAction: action => {
          $subj.next(action);
        }
      })
    );

    return $subj;
  }

  subscribeEditedMessages(): Observable<CometChat.BaseMessage> {
    const listenerID = '' + Math.random();
    const $subj = new Subject<CometChat.BaseMessage>();

    CometChat.addMessageListener(
      listenerID,
      new CometChat.MessageListener({
        onMessageEdited: customMessage => {
          $subj.next(customMessage);
        }
      })
    );

    return $subj;
  }

  public markAsRead(messageId: string, guid: string, uid: string) {
    const receiverType = 'group';
    this.markedAsReadMessageGroup[guid] = Math.max(+messageId, this.markedAsReadMessageGroup[guid] || 0);
    CometChat.markAsRead(messageId, guid, receiverType, uid);
    delete this.unreadMessages[guid];
    this.$readStatusChange.next(messageId);
  }

  public async deleteMessage(messageId: string) {
    try {
      await CometChat.deleteMessage(messageId);
    } catch (e) {
      console.error(e);
    }
  }

  public startTyping(guid: string) {
    const typingNotification = new CometChat.TypingIndicator(guid, CometChat.RECEIVER_TYPE.GROUP);
    CometChat.startTyping(typingNotification);
    clearTimeout(this.typingTimeout);
    this.typingTimeout = setTimeout(() => {
      CometChat.endTyping(typingNotification);
    }, 3000);
  }

  public async sendMediaMessage(guid: string, messageMedia: IFileToSend): Promise<void | CometChat.BaseMessage> {
    let messageType;

    if (messageMedia.type.split('/')[0] === 'image') {
      messageType = CometChat.MESSAGE_TYPE.IMAGE;
    } else if (messageMedia.type.split('/')[0] === 'video') {
      messageType = CometChat.MESSAGE_TYPE.VIDEO;
    } else {
      messageType = CometChat.MESSAGE_TYPE.FILE;
    }
    const receiverType = CometChat.RECEIVER_TYPE.GROUP;
    const mediaMessage = new CometChat.MediaMessage(guid, messageMedia.file, messageType, receiverType);

    if (messageMedia.metadata) {
      mediaMessage.setMetadata(messageMedia.metadata);
    }

    // For some reason setData overwrites metadata in this case for MediaMessage
    if (messageMedia.name) {
      const data: any = {
        name: messageMedia.name
      };
      if (messageMedia.metadata) {
        data.metadata = messageMedia.metadata;
      }
      mediaMessage.setData(data);
    }
    try {
      return await CometChat.sendMessage(mediaMessage);
    } catch (e) {
      console.error(e);
    }
  }
  public storeCache(cometUid, guid, messages) {
    const key = cometUid + '_' + guid;
    try {
      localStorage[key] = JSON.stringify(messages);
    } catch (e) {
      if (key.indexOf(cometUid) === 0 ) {
        localStorage.removeItem(key);
      }
      console.warn(e);
    }
  }

  public getCache(key) {
    if (localStorage[key]) {
      try {
        return JSON.parse(localStorage[key]);
      } catch (e) {
        return [];
      }
    }
    return [];
  }

  private async getAllUnreadMessages(messageRequest: CometChat.MessagesRequest): Promise<CometChat.BaseMessage[]> {
    let messages = await messageRequest.fetchPrevious();

    if (messages.length === this.maxLimitMessages) {
      messages = [
        ...messages,
        ...await this.getAllUnreadMessages(messageRequest)
      ];
    }

    return messages;
  }
}
